/** Thin fetch wrapper for the Michelangelo backend (proxied at /api). */

/** Carries the HTTP status so callers (App gate) can key on 401 = signed-out. */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch { /* ignore */ }
    throw new ApiError(res.status, msg);
  }
  return res.json() as Promise<T>;
}

/** GET a JSON endpoint (cookie-bearing). */
export async function apiFetch<T>(path: string): Promise<T> {
  return handle<T>(await fetch(path, { credentials: 'include' }));
}

/** POST a JSON body to an endpoint (cookie-bearing). */
export async function apiAction<T>(path: string, body?: unknown): Promise<T> {
  return handle<T>(
    await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
}

export interface AuthMe {
  email: string;
}

export interface BrandConfig {
  companyName: string;
  primaryColor: string;
  accentColor?: string;
  textColor?: string;
  fontFamily?: string;
  logoAssetRef?: string;
  wordmark?: string;
}

export interface Customer {
  id: string;
  name: string;
  industry?: string;
  brand: BrandConfig;
  facts?: string[];
}

export interface OnePagerRequest {
  customer: Customer;
  brief: string;
  audience?: 'client-facing' | 'internal';
}

export interface GenerateResult {
  /** object URL for the generated PDF blob */
  pdfUrl: string;
  warnings: string[];
}

async function jsonOrThrow(res: Response): Promise<unknown> {
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

/** POST a one-pager brief → receive a PDF blob, return an object URL. */
export async function generateOnePager(req: OnePagerRequest): Promise<GenerateResult> {
  const res = await fetch('/api/generate/one-pager', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  const warningsHeader = res.headers.get('x-studio-warnings');
  const warnings = warningsHeader ? JSON.parse(decodeURIComponent(warningsHeader)) : [];
  const blob = await res.blob();
  return { pdfUrl: URL.createObjectURL(blob), warnings };
}

export interface ContentAsset {
  id: string;
  title: string;
  description?: string;
  type?: string;
  category?: string;
  tags?: string[];
  fileUrl?: string;
  thumbnail?: string;
  audience?: string;
}

/** List contenthub assets (via the backend resolver — fixture or live). */
export async function listAssets(category?: string): Promise<ContentAsset[]> {
  const url = category ? `/api/assets?category=${encodeURIComponent(category)}` : '/api/assets';
  const data = (await jsonOrThrow(await fetch(url))) as { assets: ContentAsset[] };
  return data.assets;
}
